import React from 'react';
import { cn } from '@/lib/utils';

interface ButtonSpinnerProps {
  /**
   * Spinner border color class (default: "border-white")
   * @example "border-white", "border-gray-900", "border-primary"
   */
  className?: string;
  /**
   * Spinner size class (default: "h-4 w-4")
   * @example "h-4 w-4", "h-5 w-5"
   */
  size?: string;
  /**
   * Additional CSS classes
   */
  additionalClassName?: string;
}

/**
 * Reusable loading spinner for buttons.
 *
 * Provides consistent spinner styling across the application.
 * Used in buttons, forms, and other interactive components to indicate loading state.
 *
 * @param className - Border color class (default: "border-white")
 * @param size - Size class (default: "h-4 w-4")
 * @param additionalClassName - Additional CSS classes
 *
 * @example
 * ```tsx
 * <Button disabled={isLoading}>
 *   {isLoading ? (
 *     <>
 *       <ButtonSpinner />
 *       Processing...
 *     </>
 *   ) : (
 *     'Submit'
 *   )}
 * </Button>
 * ```
 *
 * @example
 * ```tsx
 * <Button disabled={isLoading}>
 *   {isLoading ? (
 *     <>
 *       <ButtonSpinner className="border-gray-900" size="h-5 w-5" />
 *       Loading...
 *     </>
 *   ) : (
 *     'Save'
 *   )}
 * </Button>
 * ```
 */
export function ButtonSpinner({
  className = 'border-white',
  size = 'h-4 w-4',
  additionalClassName,
}: ButtonSpinnerProps) {
  return (
    <div
      className={cn(
        'inline-block animate-spin rounded-full border-2 border-solid border-r-transparent mr-2',
        size,
        className,
        additionalClassName
      )}
      aria-hidden="true"
    />
  );
}
